/**
 * Created by Ning Jiang on 2021-05-27.
 */

import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {IReelStateEventData, ReelState} from "../../../core/reel/ReelState";
import {OktoberfestSound} from "../../sound/OktoberfestSound";
import {Oktoberfest} from "../../Oktoberfest";
import {OktoberfestSymbol} from "../../symbol/OktoberfestSymbol";
import {OktoberfestSymbolState} from "../../symbol/OktoberfestSymbolConfig";
import {GameHelper} from "../../../core/utils/GameHelper";
import {OktoberfestGameMode} from "../../server/OktoberfestGameMode";

export class ReelStopSoundController extends ReelStopPresentationController {

    private _spinStates: boolean[];

    constructor() {
        super('ReelStopSoundController');
    }

    public static playReelLandingSound(data: IReelStateEventData): boolean {
        const reels: string[][] = Oktoberfest.model.parsedGameData.reels;
        if (data.reelId === 4 && Oktoberfest.model.parsedGameData.scatterPositions![0] > -1 && Oktoberfest.model.parsedGameData.scatterPositions![2] > -1
            && Oktoberfest.model.parsedGameData.scatterPositions![4] === -1
            && Oktoberfest.model.parsedGameData.mode !== OktoberfestGameMode.FREESPIN) {
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_ANTICIPATION_FAIL);
        }
        if (data.reelId === 3 && GameHelper.isReel2Stacked(reels) && !GameHelper.isReel4Stacked(reels)) {
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_ANTICIPATION_FAIL);
            return false;
        }
        if (data.reelId === 1 && GameHelper.isReel2Stacked(reels)) {
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_STACKED_REEL_LANDING);
            return true;
        }
        if (data.reelId === 3 && GameHelper.isReel2Stacked(reels) && GameHelper.isReel4Stacked(reels)) {
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_STACKED_REEL_LANDING);
            OktoberfestSound.switchMusic({name: OktoberfestSound.getMusicNameByMode("WILD_RESPIN")});
            return true;
        }
        return false;
    }

    public async start(): Promise<any> {
        this._spinStates = Oktoberfest.reels.map(reel => reel.reelState === ReelState.SPIN);
        await super.start();
    }

    protected onReelStopped(data: IReelStateEventData): Promise<void> {
        this._spinStates[data.reelId] = false;
        if (data.reelId === 4) {
            Oktoberfest.foreground.playFrillAnimationDown();
        }
        return super.onReelStopped(data);
    }

    protected async onReelBounce(data: IReelStateEventData): Promise<any> {
        if (data.reelId === 4) {
            OktoberfestSound.soundPlayer.stopTrack(OktoberfestSound.REELS_SPINNING);
        }
        let symbol: OktoberfestSymbol = Oktoberfest.reels[data.reelId].getSymbol(1, true);
        let lastSymbol: OktoberfestSymbol = Oktoberfest.reels[data.reelId].getSymbol(5, true);
        if (symbol.symbolName.indexOf("*") === -1 || Oktoberfest.reels[data.reelId].getStackedSymbolIndex(1, true) === 2) {
            Oktoberfest.reels[data.reelId].getSymbol(1, true).getStateAnimation(
                OktoberfestSymbolState.NO_WIN,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
        if (lastSymbol.symbolName.indexOf("*") === -1 || Oktoberfest.reels[data.reelId].getStackedSymbolIndex(5, true) === 0) {
            Oktoberfest.reels[data.reelId].getSymbol(5, true).getStateAnimation(
                OktoberfestSymbolState.NO_WIN,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
        //#610 (top unmasked symbols
        if (!ReelStopSoundController.playReelLandingSound(data)) {
            OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.REELS_LANDING[data.reelId]});
        }
        const isPartyWin: boolean = Oktoberfest.model.parsedGameData.mode == "STACKED_PARTY_SPIN";
        const partySymbol: string = isPartyWin ? Oktoberfest.model.parsedGameData.stackedModeSymbol : "";
        const isPretzelParty: boolean = isPartyWin && partySymbol === "W";
        if (isPartyWin) {
            if (isPretzelParty) {
                let soundPlayed: boolean = false;
                Oktoberfest.model.parsedGameData.overlayWilds[data.reelId].forEach((symName, symIndex) => {
                    if (symName == "W" && !soundPlayed) {
                        // play when there is overlay wild.
                        OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_PM_STACKED_REEL_LANDING});
                        soundPlayed = true;
                    }
                });
            } else {
                let toPlayStackSound = true;
                //If last anticipation reel, play fail sound. Except when its the last reel.
                let lastNearWinReel = Oktoberfest.model.parsedGameData.nearWinReels.lastIndexOf(true);
                if (lastNearWinReel != -1 && lastNearWinReel == data.reelId && data.reelId != 4) {
                    OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_ANTICIPATION_FAIL});
                }

                //IF not first reel AND is not a near win reel we don't play spacial landing sound even when stacked
                if (data.reelId > 0 && !Oktoberfest.model.parsedGameData.nearWinReels[data.reelId]) {
                    toPlayStackSound = false;
                }

                //Don't play special sound if not stacked party symbol.
                for (let i: number = 0; i < 3; i++) {
                    if (Oktoberfest.model.parsedGameData.reelData[data.reelId][i] != partySymbol) {
                        toPlayStackSound = false;
                    }
                }
                // only play sound when all symbols are the stacked symbols.
                if (toPlayStackSound) {
                    OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_PM_STACKED_REEL_LANDING});
                }
            }
        }
        return Promise.resolve();
    }
}
